import React, { useRef, useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';

/**
 * CodeEditor Component
 * 
 * A wrapper around Monaco Editor for editing code cells with Python syntax highlighting,
 * auto-completion, and other IDE features.
 */
function CodeEditor({ 
  value, 
  onChange, 
  onBlur, 
  onKeyDown,
  language = 'python',
  readOnly = false,
  theme = 'vs-light'
}) {
  const editorRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState('300px');

  // Calculate height based on text length
  useEffect(() => {
    if (value) {
      // Count lines in the code
      const lineCount = value.split('\n').length;
      // Calculate height: ~20px per line + padding
      const calculatedHeight = Math.max(80, Math.min(lineCount * 20 + 20, 800));
      setEditorHeight(`${calculatedHeight}px`);
    } else {
      setEditorHeight('300px');
    }
  }, [value]);

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;

    // Add Ctrl+Enter / Cmd+Enter shortcut
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
       if (onKeyDown) {
           // Simulate a partial event object or just call the handler
           // passing a custom flag or object that the parent expects
           onKeyDown({ key: 'Enter', ctrlKey: true, metaKey: true, preventDefault: () => {} });
       }
    });
  };

  const handleEditorChange = (newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleEditorBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const handleKeyDown = (e) => {
    // Pass keyboard events up to parent if handler provided
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div className="monaco-editor-wrapper border border-gray-300 rounded overflow-hidden">
      <Editor
        onMount={handleEditorMount}
        height={editorHeight}
        language={language}
        value={value}
        onChange={handleEditorChange}
        onBlur={handleEditorBlur}
        onKeyDown={handleKeyDown}
        theme={theme}
        options={{
          minimap: { enabled: false },
          fontSize: 13,
          fontFamily: "'Fira Code', 'Monaco', 'Courier New', monospace",
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 4,
          insertSpaces: true,
          wordWrap: 'on',
          contextmenu: true,
          suggestOnTriggerCharacters: true,
          quickSuggestions: {
            other: true,
            comments: false,
            strings: false,
          },
          readOnly: readOnly,
          padding: { top: 10, bottom: 10 },
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
          folding: true,
          foldingHighlight: true,
        }}
      />
    </div>
  );
}

export default CodeEditor;
